import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/cn';

/**
 * The portal's buttons. `default` is the light blue fill with navy text used
 * for most actions; `primary` is the navy fill kept for the one most important
 * action on a page (Sign in, File to the IRS).
 */
export type ButtonVariant = 'default' | 'primary' | 'outline' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap ' +
  'transition-[background-color,color,box-shadow,transform,border-color] duration-150 ease-out select-none ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ts-focus-ring)] ' +
  // A disabled button keeps its pointer events so the cursor can say "not allowed".
  // The browser swallows the click either way.
  'active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 disabled:shadow-none';

// Hover is written as enabled:hover so a disabled button stays flat while it
// still shows the not-allowed cursor.
const variants: Record<ButtonVariant, string> = {
  // The portal's light blue button turns navy with white text on hover.
  default: 'bg-primary-light text-primary enabled:hover:bg-primary enabled:hover:text-primary-foreground',
  primary:
    'bg-primary text-primary-foreground shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)] ' +
    'enabled:hover:bg-[color:var(--ts-primary-hover)]',
  // The portal's outline button is drawn in the primary navy, edge and text both.
  outline:
    'border border-primary bg-surface text-primary ' +
    'enabled:hover:bg-[color:var(--ts-row-hover)]',
  ghost: 'text-gray-3 enabled:hover:bg-[color:var(--ts-row-hover)] enabled:hover:text-primary',
  destructive: 'text-destructive enabled:hover:bg-[color:var(--ts-pill-danger-bg)]',
};

const sizes: Record<ButtonSize, string> = {
  // The portal's heights: 36px in a form, 40px beside a filter or on a sign-in page.
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-4 text-base',
  lg: 'h-10 px-5 text-base',
  icon: 'h-9 w-9 rounded-full p-0',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'md', asChild = false, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(base, variants[variant], sizes[size], className)}
      {...props}
    />
  );
});

/** Text that behaves as a link: the portal's #0671E0, 14px medium. */
export const LinkButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  function LinkButton({ className, type, ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn('text-base font-medium text-link hover:underline', className)}
        {...props}
      />
    );
  },
);
